import React from 'react'

import {
  faAngleDoubleDown,
  faAngleDoubleUp,
  faEdit,
  faPlus,
  faSave,
  faTimes,
  faTrashAlt
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Data from 'data'
import Store from 'store'
import Select, { OptionOrGroup, SelectOption, underlinerFunc } from 'ui/select'

import Dialog from 'ui/dialog'
import { OverlayContext } from 'ui/utils/OverlayManager'
import { searchRegex, versionNumberToString } from 'utils'
import './BuildList.scss'
import { MarkdownEditor, RenderMarkdown } from './MarkdownEditor'

import { validateState } from '@/logic/validation'
import { CallbackWrap } from '@/types/common'
import { BuildListEmbed, BuildListEntry } from '@/types/embed'
import { PlayerClass } from '@/types/profile'
import { faTwitch, faYoutube } from '@fortawesome/free-brands-svg-icons'
import { getUniqueId } from '@maxroll-media-group/common-libs'
import useApi, { Api } from 'api'
import { BuildListProfile } from 'common'
import Trans from 'data/trans'
import { evaluateTags, modStatsMax } from 'logic/mods'
import Tooltip from 'ui/tooltip'

const defaultBuild = (): BuildListEntry => ({
  id: getUniqueId(),
  charClass: 'StrDexInt',
  ascendancy: 'Ascendant',
  title: 'New Build',
  patch: Math.max(...Data.versions),
  categories: [],
  description: 'PoB Links: [Leveling](), [Mid-game](), [Endgame]()\n\nFarming strategies: ',
  mods: []
})

const Categories: Record<string, string> = {
  bossing: 'Bossing',
  mapping: 'Mapping',
  starter: 'League Starter',
  endgame: 'Endgame',
  softcore: 'Softcore',
  hardcore: 'Hardcore',
  caster: 'Caster',
  melee: 'Melee',
  minions: 'Minions',
  ranged: 'Ranged',
  totems: 'Totems',
  traps: 'Traps & Mines',
  dot: 'Damage Over Time',
  physical: 'Physical',
  lightning: 'Lightning',
  cold: 'Cold',
  fire: 'Fire',
  chaos: 'Chaos'
}

export const banditOptions = [
  {
    id: 'all',
    name: 'Kill All',
    stats: ['2 Passive Skill Points']
  },
  {
    id: 'alira',
    name: 'Alira',
    stats: [
      '5 Mana Regenerated per second',
      '+20% to Global Critical Strike Multiplier',
      '+15% to all Elemental Resistances'
    ]
  },
  {
    id: 'kraitlyn',
    name: 'Kraitlyn',
    stats: [
      '6% increased Attack and Cast Speed',
      '10% chance to Avoid Elemental Ailments',
      '6% increased Movement Speed'
    ]
  },
  {
    id: 'oak',
    name: 'Oak',
    stats: [
      '1% of Life Regenerated per second',
      '2% additional Physical Damage Reduction',
      '20% increased Physical Damage'
    ]
  }
]

const StatsTip = ({ stats }: { stats: string[] }) => (
  <div className="poe-StatsTooltip">
    {stats.map((line, index) => (
      <div key={index}>{line}</div>
    ))}
  </div>
)

type ClassOption = {
  id: string
  chr: PlayerClass
  name: string
}

function SelectClass({
  charClass,
  ascendancy,
  onChange,
  allowEmpty
}: {
  charClass: PlayerClass | undefined
  ascendancy?: string
  onChange: (value: PlayerClass | undefined, ascendancy: string | undefined) => void
  allowEmpty?: boolean
}) {
  const character = (charClass && Data.characters[charClass]) || Data.characters.StrDexInt
  const ascendancyData = (ascendancy && character.ascendancies[ascendancy]) || Object.values(character.ascendancies)[0]
  const getOptions = React.useCallback(
    (): OptionOrGroup<ClassOption>[] =>
      Object.entries(Data.characters).map(([chrId, character]) => ({
        title: character.name,
        options: Object.entries(character.ascendancies).map(([ascId, ascendancy]) => ({
          id: ascId,
          chr: chrId as PlayerClass,
          name: ascendancy.name
        }))
      })),
    []
  )
  const onSelect = React.useCallback((opt?: ClassOption) => onChange(opt?.chr, opt?.id), [onChange])
  return (
    <Select
      options={getOptions}
      value={
        ascendancy
          ? {
              id: ascendancy,
              chr: charClass ?? 'StrDexInt',
              name: ascendancyData.name
            }
          : undefined
      }
      className="ui-Select"
      onChange={onSelect}
      allowEmpty={allowEmpty}
    />
  )
}

type BuildFilter = {
  charClass?: PlayerClass
  ascendancy?: string
  title?: string
  titleRegex?: RegExp
  patch?: number
  categories?: string[]
}

function filterBuild(build: BuildListEntry, filter: BuildFilter) {
  if (filter.charClass && build.charClass !== filter.charClass) return false
  if (filter.ascendancy && build.ascendancy !== filter.ascendancy) return false
  if (filter.titleRegex && !build.title.match(filter.titleRegex)) return false
  if (filter.patch && build.patch !== filter.patch) return false
  if (filter.categories && filter.categories.some((cat) => !build.categories.includes(cat))) return false
  return true
}

function CategoryEditor({
  value,
  onChange
}: {
  value: string[]
  onChange: (value: (prev: string[] | undefined) => string[]) => void
}) {
  const options = React.useMemo(() => Object.entries(Categories).map(([id, name]) => ({ id, name })), [])
  const renderValue = React.useCallback(
    () =>
      options
        .filter((opt) => value.includes(opt.id))
        .map((opt) => {
          function onClick(e: React.MouseEvent) {
            e.stopPropagation()
            onChange((value) => {
              if (!value) return []
              const index = value.indexOf(opt.id)
              if (index < 0) return value
              value = value.slice()
              value.splice(index, 1)
              return value
            })
          }
          return (
            <span key={opt.id} className={`poe-value poe-category-${opt.id}`}>
              {opt.name} <FontAwesomeIcon className="poe-remove" icon={faTimes} onClick={onClick} />
            </span>
          )
        }),
    [options, value, onChange]
  )
  const renderOption = React.useCallback(
    ({ id, name }: SelectOption<string>) => <span className={`poe-category-${id}`}>{name}</span>,
    []
  )
  const onSelect = React.useCallback(
    (opt?: SelectOption<string>) =>
      opt &&
      onChange((value) => {
        if (!value) return [opt.id]
        if (value.includes(opt.id)) return value
        return [...value, opt.id]
      }),
    [onChange]
  )
  return (
    <Select
      className="poe-BuildCategory"
      valueClass="ui-Select-value"
      options={options}
      value={options.find((opt) => value.includes(opt.id))}
      onChange={onSelect}
      renderValue={renderValue}
      renderOption={renderOption}
      placeholder="Categories..."
    />
  )
}

function PatchEditor({
  value,
  onChange,
  allowEmpty
}:
  | {
      value?: number
      onChange: (value: number) => void
      allowEmpty?: false
    }
  | {
      value: number | undefined
      onChange: (value: number | undefined) => void
      allowEmpty: true
    }) {
  const options = React.useMemo(() => Data.versions.map((v) => ({ id: v, name: versionNumberToString(v) })), [])
  const onSelect = React.useCallback(
    (opt?: SelectOption<number>) => (allowEmpty ? onChange(opt?.id) : opt && onChange(opt.id)),
    [onChange, allowEmpty]
  )
  return (
    <Select
      className="poe-BuildVersion"
      options={options}
      value={options.find((opt) => opt.id === value)}
      allowEmpty={allowEmpty}
      placeholder="Select version..."
      onChange={onSelect}
    />
  )
}

function BuildFilters({
  filters,
  onChange
}: {
  filters: BuildFilter
  onChange: (value: (prev: BuildFilter) => BuildFilter) => void
}) {
  const onChangeField =
    <K extends keyof BuildFilter>(field: K, defaultValue?: BuildFilter[K]) =>
    (value: CallbackWrap<BuildFilter[K]>) =>
      onChange((build) => {
        if (typeof value === 'function') value = value(build[field] ?? defaultValue)
        if (value === (build[field] ?? defaultValue)) return build
        return { ...build, [field]: value }
      })
  const onChangeTitle = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange((build) => ({
        ...build,
        title: e.target.value,
        titleRegex: searchRegex(e.target.value, true)
      }))
    },
    [onChange]
  )
  const onChangeClass = React.useCallback(
    (charClass?: PlayerClass, ascendancy?: string) => onChange((value) => ({ ...value, charClass, ascendancy })),
    [onChange]
  )
  return (
    <div className="poe-BuildFilters">
      <div className="poe-main">
        <div className="poe-box-row">
          <SelectClass
            charClass={filters.charClass}
            ascendancy={filters.ascendancy}
            onChange={onChangeClass}
            allowEmpty
          />
          <PatchEditor value={filters.patch} onChange={onChangeField('patch')} allowEmpty />
        </div>
        <div className="poe-box-row">
          <CategoryEditor value={filters.categories || []} onChange={onChangeField('categories', [])} />
        </div>
        <div className="poe-text-row">
          <input type="text" value={filters.title || ''} onChange={onChangeTitle} placeholder="Search" />
        </div>
      </div>
    </div>
  )
}

function BuildViewer({ build, search, onEdit }: { build: BuildListEntry; search?: RegExp; onEdit?: () => void }) {
  const [expanded, setExpanded] = React.useState(false)
  const character = Data.characters[build.charClass] || Data.characters.StrDexInt
  const ascendancy =
    (build.ascendancy && character.ascendancies[build.ascendancy]) || Object.values(character.ascendancies)[0]

  const categories = []
  for (const cat of build.categories) {
    if (categories.length) categories.push(', ')
    categories.push(
      <span key={cat} className={`poe-category-${cat}`}>
        {Categories[cat]}
      </span>
    )
  }

  return (
    <div className="poe-BuildViewer">
      <div className="poe-header">
        <div className={`poe-portrait poe-${build.ascendancy}`} title={ascendancy.name} />
        <div className="poe-main">
          {!!onEdit && (
            <div className="poe-button" onClick={onEdit}>
              <FontAwesomeIcon icon={faEdit} />
            </div>
          )}
          <div className="poe-title">{underlinerFunc(build.title, search)}</div>
          <div className="poe-info">
            <div className="poe-block">Patch {versionNumberToString(build.patch)}</div>
            {!!build.categories.length && <div className="poe-block">{categories}</div>}
          </div>
          <div className="poe-markdown">
            <RenderMarkdown value={build.description} />
          </div>
          <div className="poe-extra">
            {!!(build.twitch || build.youtube) && (
              <div className="poe-links">
                {!!build.twitch && (
                  <a href={build.twitch} target="_blank" rel="noopener noreferrer" className="poe-twitch">
                    <FontAwesomeIcon icon={faTwitch} />
                  </a>
                )}
                {!!build.youtube && (
                  <a href={build.youtube} target="_blank" rel="noopener noreferrer" className="poe-youtube">
                    <FontAwesomeIcon icon={faYoutube} />
                  </a>
                )}
              </div>
            )}
            {build.mods.length > 0 && (
              <div className="poe-toggle" onClick={() => setExpanded(!expanded)}>
                {expanded ? (
                  <>
                    Hide details <FontAwesomeIcon icon={faAngleDoubleUp} />
                  </>
                ) : (
                  <>
                    Show details <FontAwesomeIcon icon={faAngleDoubleDown} />
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {!!expanded && (
        <div className="poe-mods">
          <h3>Map modifiers to avoid:</h3>
          <ul>
            {build.mods.map((id, index) => (
              <div key={index}>
                {renderModOption({
                  id,
                  name: Data.mods[id].name,
                  stats: modStatLines(id)
                })}
              </div>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function DeleteConfirmation({
  title,
  onResolve,
  onClose
}: {
  title: string
  onResolve?: (value: boolean) => void
  onClose?: () => void
}) {
  return (
    <Dialog title={`Delete build`} modal onClose={onClose!}>
      <Dialog.Content>
        <p>{`Are you sure you want to delete ${title}?`}</p>
      </Dialog.Content>
      <Dialog.Footer>
        <button onClick={() => onResolve!(true)}>{`Yes`}</button>
        <button onClick={() => onResolve!(false)}>{`No`}</button>
      </Dialog.Footer>
    </Dialog>
  )
}

function modStatLines(id: string) {
  const stats = modStatsMax(Data.mods[id])
  delete stats['map_item_drop_quantity_+%']
  delete stats['map_item_drop_rarity_+%']
  delete stats['map_pack_size_+%']
  return Trans.areas.formatStats(stats)
}

type ModOption = {
  id: string
  name: string
  stats: string[]
}

const ModTooltip = ({ id }: { id: string }) => <StatsTip stats={modStatLines(id)} />
const renderModValue = ({ id, name }: ModOption) => (
  <Tooltip tooltip={<ModTooltip id={id} />}>
    <span>{name}</span>
  </Tooltip>
)
const renderModOption = ({ name, stats }: ModOption, search?: RegExp) => (
  <div className="poe-mod-option">
    <div className="poe-title">{underlinerFunc(name, search)}</div>
    {stats.map((line, index) => (
      <div key={index} className="poe-stat">
        {underlinerFunc(line, search)}
      </div>
    ))}
  </div>
)
const modSearch = ({ name, stats }: ModOption, search: RegExp) =>
  name.match(search) || stats.some((stat) => stat.match(search))

function BuildEditor({
  build,
  onChange,
  editing,
  setEditing
}: {
  build: BuildListEntry
  onChange: (value: CallbackWrap<BuildListEntry> | undefined) => void
  editing: boolean
  setEditing: (value: boolean) => void
}) {
  const getModifiers = React.useCallback(() => {
    const prefix: ModOption[] = [],
      suffix: ModOption[] = []
    const tags = new Set(['forest', 'swamp', 'urban', 'top_tier_map', 'not_for_sale', 'map', 'default'])
    for (const [id, mod] of Object.entries(Data.mods)) {
      if (
        mod.domain === 'area' &&
        ['suffix', 'prefix'].includes(mod.generation_type) &&
        evaluateTags(mod.spawn_weights, tags)
      ) {
        const stats = modStatLines(id)
        if (mod.generation_type === 'suffix') {
          suffix.push({ id, name: mod.name, stats })
        } else {
          prefix.push({ id, name: mod.name, stats })
        }
      }
    }
    return [
      { title: 'Prefixes', options: prefix },
      { title: 'Suffixes', options: suffix }
    ]
  }, [])
  const onChangeField =
    <K extends keyof BuildListEntry>(field: K) =>
    (value: CallbackWrap<BuildListEntry[K]>) =>
      onChange((build) => {
        if (typeof value === 'function') value = value(build[field])
        if (value === build[field]) return build
        return { ...build, [field]: value }
      })
  const overlays = React.useContext(OverlayContext)
  const onDelete = React.useCallback(async () => {
    if (await overlays.async(<DeleteConfirmation title={build.title} />)) {
      onChange(undefined)
    }
  }, [build.title, overlays, onChange])

  const onSelectMod = (index: number, opt?: ModOption) =>
    onChange((build) => {
      build = { ...build }
      build.mods = build.mods.slice()
      if (!opt) {
        build.mods.splice(index, 1)
      } else {
        build.mods[index] = opt.id
      }
      return build
    })
  const onChangeClass = React.useCallback(
    (charClass?: PlayerClass, ascendancy?: string) =>
      charClass && onChange((value) => ({ ...value, charClass, ascendancy })),
    [onChange]
  )
  if (!editing) return <BuildViewer build={build} onEdit={() => setEditing(true)} />
  return (
    <div className="poe-BuildEditor">
      <div className="poe-main">
        <div className="poe-box-row">
          <SelectClass charClass={build.charClass} ascendancy={build.ascendancy} onChange={onChangeClass} />
          <PatchEditor value={build.patch} onChange={onChangeField('patch')} />
          <div className="poe-button" onClick={() => setEditing(false)}>
            <FontAwesomeIcon icon={faSave} />
          </div>
          <div className="poe-button" onClick={onDelete}>
            <FontAwesomeIcon icon={faTrashAlt} />
          </div>
        </div>
        <div className="poe-text-row">
          <input
            type="text"
            value={build.title}
            onChange={(e) => onChange((build) => ({ ...build, title: e.target.value }))}
          />
        </div>
        <div className="poe-box-row">
          <CategoryEditor value={build.categories} onChange={onChangeField('categories')} />
        </div>
        <div className="poe-text-row">
          <FontAwesomeIcon icon={faTwitch} className="poe-twitch" />
          <input
            type="text"
            value={build.twitch || ''}
            onChange={(e) => onChange((build) => ({ ...build, twitch: e.target.value }))}
            placeholder="Twitch Link"
          />
        </div>
        <div className="poe-text-row">
          <FontAwesomeIcon icon={faYoutube} className="poe-youtube" />
          <input
            type="text"
            value={build.youtube || ''}
            onChange={(e) => onChange((build) => ({ ...build, youtube: e.target.value }))}
            placeholder="Youtube Link"
          />
        </div>
        <MarkdownEditor value={build.description} onChange={onChangeField('description')} />
      </div>
      <div className="poe-mods">
        <h3>Map modifiers to avoid:</h3>
        <ul>
          {build.mods.map((id, index) => (
            <li key={index}>
              <Select
                className="ui-Select"
                options={getModifiers}
                value={{ id, name: Data.mods[id]?.name, stats: [] }}
                renderOption={renderModOption}
                renderValue={renderModValue}
                searchLimit={10}
                searchMiddle
                searchFunc={modSearch}
                onChange={(opt) => onSelectMod(index, opt)}
                allowEmpty
              />
            </li>
          ))}
          <li>
            <Select
              className="ui-Select"
              value={undefined}
              options={getModifiers}
              renderOption={renderModOption}
              renderValue={renderModValue}
              searchLimit={10}
              searchMiddle
              searchFunc={modSearch}
              onChange={(opt) => onSelectMod(build.mods.length, opt)}
            />
          </li>
        </ul>
      </div>
    </div>
  )
}

async function saveBuildList(api: Api, id: string, value: BuildListEntry | undefined) {
  const response = await api.load(BuildListProfile)
  const latest = await validateState(JSON.parse(response.data))
  const embed = latest.embeds.find((e) => e.type === 'buildlist') as BuildListEmbed | undefined
  if (!embed) return
  const index = embed.builds.findIndex((b) => b.id === id)
  if (index >= 0) {
    if (value) {
      embed.builds[index] = value
    } else {
      embed.builds.splice(index, 1)
    }
  } else if (value) {
    embed.builds.push(value)
  }
  await api.save({
    name: 'Build List',
    folder: 0,
    id: BuildListProfile,
    data: JSON.stringify(latest)
  })
}

export default function BuildListEditor() {
  const [filters, setFilters] = React.useState<BuildFilter>({
    patch: Data.defaultVersion
  })
  const [editing, setEditing] = React.useState<Record<string, boolean>>({})

  const api = useApi()
  const embed = Store.useEmbed() as BuildListEmbed
  const dispatch = Store.useDispatch()

  const onCreate = React.useCallback(() => {
    const newBuild = defaultBuild()
    const newEmbed = { ...embed }
    newEmbed.builds = newEmbed.builds.slice()
    newEmbed.builds.push(newBuild)
    dispatch.setEmbed(newEmbed)
    setEditing((editing) => ({ ...editing, [newBuild.id]: true }))
  }, [dispatch, embed])

  return (
    <div className="poe-BuildList">
      <BuildFilters filters={filters} onChange={setFilters} />
      <div className="poe-create" onClick={onCreate}>
        <FontAwesomeIcon icon={faPlus} /> Create
      </div>
      <ul className="poe-list">
        {embed.builds.map((build, index) => {
          if (!filterBuild(build, filters)) return null
          const onChange = (value: CallbackWrap<BuildListEntry> | undefined) => {
            dispatch.setEmbed((embed) => {
              if (embed.type !== 'buildlist') return embed
              const prev = embed.builds[index]
              if (typeof value === 'function') value = value(prev)
              if (value === prev) return embed
              embed = { ...embed }
              embed.builds = embed.builds.slice()
              if (value) {
                embed.builds[index] = value
              } else {
                embed.builds.splice(index, 1)
              }
              return embed
            })
            if (!value) {
              saveBuildList(api, build.id, undefined)
            }
          }
          const onEdit = (flag: boolean) => {
            setEditing((editing) => ({ ...editing, [build.id]: flag }))
            if (!flag) saveBuildList(api, build.id, build)
          }
          return (
            <BuildEditor
              key={build.id}
              build={build}
              onChange={onChange}
              editing={editing[build.id]}
              setEditing={onEdit}
            />
          )
        })}
      </ul>
    </div>
  )
}

export function BuildListTool({ data }: { data: BuildListEmbed }) {
  const [filters, setFilters] = React.useState<BuildFilter>({
    patch: Data.defaultVersion
  })

  const entries = data.builds.filter((build) => filterBuild(build, filters))

  return (
    <div className="poe-BuildList">
      <BuildFilters filters={filters} onChange={setFilters} />
      <ul className="poe-list">
        {entries.map((build) => (
          <BuildViewer key={build.id} build={build} search={filters.titleRegex} />
        ))}
        {!entries.length && <div className="poe-notice">No results</div>}
      </ul>
    </div>
  )
}
